import {
  Box,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  ScrollArea,
  Text,
  Video
} from "@prismicio/editor-ui"
import { useState } from "react"

import type { OnboardingStep } from "../../service"

interface OnboardingStepDialogProps {
  isOpen: boolean
  step: OnboardingStep
  stepIndex: number
  isStepComplete: boolean
  onActionClick: () => Promise<void>
  onClose: () => void
}

export function OnboardingStepDialog(props: OnboardingStepDialogProps) {
  const { step, stepIndex, isOpen, isStepComplete, onActionClick, onClose } =
    props

  const [isToggling, setToggling] = useState(false)

  const [actionButtonLabel, setActionButtonLabel] =
    useState(getActionButtonLabel)

  function getActionButtonLabel() {
    return isStepComplete ? "Undo step" : "Mark as done"
  }

  function updateActionButtonLabel() {
    if (!isOpen) return
    setActionButtonLabel(getActionButtonLabel())
  }

  async function handleAction() {
    try {
      setToggling(true)
      await onActionClick()
    } finally {
      setToggling(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
      onAnimationEnd={updateActionButtonLabel}
      onAnimationStart={updateActionButtonLabel}
      size="small"
    >
      <DialogHeader title="Onboarding" />
      <DialogContent>
        <ScrollArea>
          <Box as="article" flexDirection="column" padding={16} gap={16}>
            <section>
              <Text sx={{ marginBottom: 4 }} color="purple9" variant="bold">
                Step {stepIndex}
              </Text>
              <Text variant="h3">{step.title}</Text>
              {step.content}
            </section>
            {step.videoUrl && (
              <Video src={step.videoUrl} sizing="contain" autoPlay loop />
            )}
          </Box>
        </ScrollArea>
        <DialogActions>
          <DialogCancelButton size="medium">Close</DialogCancelButton>
          {!step.defaultCompleted && (
            <DialogActionButton
              size="medium"
              onClick={handleAction}
              loading={isToggling}
            >
              {actionButtonLabel}
            </DialogActionButton>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}
